export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    credentials: 'include',
    headers: init?.body ? { 'content-type': 'application/json' } : undefined,
    ...init,
  });
  if (!response.ok) {
    const detail = await response.json().catch(() => ({}) as { message?: string });
    throw new ApiError(detail.message ?? response.statusText, response.status);
  }
  return response.status === 204 ? (undefined as T) : ((await response.json()) as T);
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PUT', body: body === undefined ? undefined : JSON.stringify(body) }),
  del: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};

export interface BuildInfo {
  status: string;
  uptimeSeconds: number;
  version: string;
  builtAt: string | null;
}

export interface FailureRow {
  failure: string | null;
  runs: number;
}

export interface WorkerRow {
  id: string;
  host: string;
  tenants: string[];
  maxParallel: number;
  running: number;
  version: string;
  builtAt: string | null;
  startedAt: string;
  seenAt: string;
}

export interface DashboardState {
  viewer: { email: string; isAdmin: boolean };
  tenants: { id: string; name: string; slug: string }[];
  queue: TaskRow[];
  running: RunRow[];
  recent: RunRow[];
  usage: UsageRow[];
  failures: FailureRow[];
  workers: WorkerRow[];
  generatedAt: string;
}

export interface TaskRow {
  id: string;
  organizationId: string;
  projectKey: string;
  title: string;
  body: string;
  status: string;
  state: string;
  notes: string;
  createdAt: string;
}

export interface RunRow {
  id: string;
  organizationId: string;
  taskId: string;
  projectKey: string;
  title: string;
  branch: string | null;
  worktree: string;
  mrUrl: string | null;
  mrError: string | null;
  startedAt: string;
  endedAt: string | null;
  ok: boolean | null;
  reason: string | null;
  tokens: number;
  turns: number;
  costUsd: number;
  /** 'subscription' | 'api' | 'unknown' — whether costUsd is money or equivalence. */
  billing: string;
  model: string;
  cancelRequested?: boolean;
}

export interface UsageRow {
  model: string;
  billing: string;
  runs: number;
  tokens: number;
  costUsd: number;
}

export interface TenantDetail {
  id: string;
  name: string;
  slug: string;
  settings: TenantSettings | null;
  projects: ProjectRow[];
  sources: SourceRow[];
  handlers: HandlerRow[];
}

export interface TenantSettings {
  organizationId: string;
  sshPublicKey: string | null;
  hasSshKey: boolean;
  hasForgeToken: boolean;
  knownHosts: string | null;
  gitUserName: string | null;
  gitUserEmail: string | null;
  notify: string;
  notifyExtra: string[];
  agentCommand: string | null;
  model: string | null;
  timeoutMinutes: number | null;
  maxRuntimeMinutes: number | null;
  maxContinuations: number | null;
  maxParallel: number | null;
  skills: string[] | null;
  prompt: string | null;
  mcpServers: Record<string, unknown> | null;
  strictMcp: boolean | null;
  worktreeRoot: string | null;
}

export interface ProjectRow {
  id: string;
  organizationId: string;
  key: string;
  repoUrl: string | null;
  path: string | null;
  /** Derived by the server; where the clone actually lives. */
  checkoutPath: string;
  baseBranch: string;
  remote: string;
  branchTemplate: string | null;
  afterCreate: string | null;
  createMr: string | null;
  push: boolean;
  model: string | null;
}

export interface SourceRow {
  id: string;
  organizationId: string;
  type: string;
  name: string;
  enabled: boolean;
  config: Record<string, unknown>;
  secret: string;
}

export interface MemberRow {
  id: string;
  role: string;
  userId: string;
  email: string;
  name: string;
  banned: boolean;
}

export interface InvitationRow {
  id: string;
  email: string;
  role: string | null;
  status: string;
  expiresAt: string;
}

export interface BrowsedIssue {
  externalId: string;
  externalUrl: string;
  title: string;
  status: string;
  projectKey: string;
  sourceId: string;
  sourceName: string;
  tenantId: string;
}

export interface HandlerRow {
  id: string;
  organizationId: string;
  status: string;
  prompt: string;
  onSuccess: string | null;
  onFailure: string | null;
  model: string | null;
  timeoutMinutes: number | null;
}
